import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/kit/ui/select";
import { ReactNode, useId } from "react";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { useNavigate } from "react-router";
import { assertNever } from "../utils/helpers";
import {
  ProjectFileBrowserFragment$key,
  ProjectVersionFileKind,
} from "./__generated__/ProjectFileBrowserFragment.graphql";
import { Path, joinPath } from "../utils/url";
import ProjectDownloadButton from "./ProjectDownloadButton";

const ProjectFileBrowserFragment = graphql`
  fragment ProjectFileBrowserFragment on Project
  @argumentDefinitions(
    kind: { type: "ProjectVersionFileKind", defaultValue: TEMPLATE }
    version: { type: "Semver" }
  ) {
    __typename
    id
    version(version: $version) {
      version
      files {
        id
        kind
      }
      fileByKind(kind: $kind) {
        id
        kind
        ...ProjectDownloadButtonFragment
      }
    }
  }
`;

export interface ProjectFileBrowserProps {
  root: Path;
  project: ProjectFileBrowserFragment$key;
  competition?: string | undefined;
  children: ReactNode;
}

export default function CompetitionFileBrowser({
  root,
  project: projectFragment,
  children,
}: ProjectFileBrowserProps) {
  const selectId = useId();
  const navigate = useNavigate();
  const project = useFragment(ProjectFileBrowserFragment, projectFragment);

  const selected = project.version?.fileByKind;
  let files = project.version?.files.concat() ?? [];
  files = files.filter((file) => file.kind in projectVersionFileKindOrder);
  files.sort(
    (lhs, rhs) =>
      projectVersionFileKindOrder[lhs.kind]! -
      projectVersionFileKindOrder[rhs.kind]!,
  );

  if (files.length <= 0) {
    return <p className="w-full py-6 text-center">No files available</p>;
  }

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row space-x-3 justify-start items-end">
          <label
            htmlFor={selectId}
            className="justify-start text-gray-900 text-xl font-semibold  leading-8"
          >
            <FormattedMessage defaultMessage="Show files in:" />
          </label>
          <Select
            value={selected?.kind}
            onChange={(value) => {
              navigate(joinPath(root, value.toLowerCase()));
            }}
          >
            <SelectTrigger id={selectId} className="px-2 py-1 w-auto border-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {files.map((file) => (
                <SelectItem key={file.kind} value={file.kind}>
                  <ProjectVersionFileKindMessage
                    kind={file.kind}
                    inUseCase={project.__typename === "UseCase"}
                  />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selected && (
          <div className="flex flex-row space-x-3 justify-end items-center">
            <ProjectDownloadButton fragment={selected} />
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

const projectVersionFileKindOrder: Partial<
  Record<ProjectVersionFileKind, number>
> = {
  TEMPLATE: 0,
  DATA: 1,
  PACKAGE: 2,
};

interface ProjectVersionFileKindMessageProps {
  kind: ProjectVersionFileKind;
  inUseCase?: boolean | undefined;
}

function ProjectVersionFileKindMessage({
  kind,
  inUseCase,
}: ProjectVersionFileKindMessageProps): React.ReactNode {
  switch (kind) {
    case "DATA":
      return <FormattedMessage defaultMessage="Data" />;
    case "PACKAGE":
      if (inUseCase) return <FormattedMessage defaultMessage="Use Case" />;
      else return <FormattedMessage defaultMessage="Submission" />;
    case "SUBMISSION_EVALUATION":
      return <FormattedMessage defaultMessage="Submission Evaluation" />;
    case "TEMPLATE":
      return <FormattedMessage defaultMessage="Submission Template" />;
    default:
      assertNever(kind);
  }
}
